import React from 'react';
import { CircleDot, AlertCircle, Hourglass, PauseCircle, CheckCircle, XCircle } from 'lucide-react';
import { TicketStatus } from '../types/tickets';

interface TicketProgressBarProps {
  currentStatus: TicketStatus;
  size?: 'sm' | 'default';
}

interface Step {
  id: number;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  statusValue: TicketStatus[];
}

export function TicketProgressBar({ currentStatus, size = 'default' }: TicketProgressBarProps) {
  // Determine the middle step label and icon based on current status
  const getMiddleStepDetails = () => {
    switch (currentStatus) {
      case TicketStatus.InProgress:
        return { label: 'In Progress', icon: Hourglass };
      case TicketStatus.Pending:
        return { label: 'Pending', icon: AlertCircle };
      case TicketStatus.OnHold:
        return { label: 'On Hold', icon: PauseCircle };
      default:
        return { label: 'In Progress', icon: Hourglass };
    }
  };

  const middleStep = getMiddleStepDetails();

  const steps: Step[] = [
    { id: 0, label: 'Open', icon: CircleDot, statusValue: [TicketStatus.Open] },
    { id: 1, label: middleStep.label, icon: middleStep.icon, statusValue: [TicketStatus.InProgress, TicketStatus.Pending, TicketStatus.OnHold] },
    { id: 2, label: 'Resolved', icon: CheckCircle, statusValue: [TicketStatus.Resolved] },
    { id: 3, label: 'Closed', icon: XCircle, statusValue: [TicketStatus.Closed] },
  ];

  // Determine which step index is currently active
  const getCurrentStepIndex = (): number => {
    switch (currentStatus) {
      case TicketStatus.Open:
        return 0;
      case TicketStatus.InProgress:
      case TicketStatus.Pending:
      case TicketStatus.OnHold:
        return 1;
      case TicketStatus.Resolved:
        return 2;
      case TicketStatus.Closed:
        return 3;
      default:
        return 0;
    }
  };

  const currentStepIndex = getCurrentStepIndex();

  // Get color classes based on step state
  const getStepColors = (stepIndex: number) => {
    if (stepIndex < currentStepIndex) {
      // Completed step
      return {
        circle: 'bg-green-500 dark:bg-green-600 text-white border-green-500 dark:border-green-600',
        text: 'text-green-700 dark:text-green-400',
        line: 'bg-green-500 dark:bg-green-600',
      };
    } else if (stepIndex === currentStepIndex) {
      // Current active step - use status-specific colors
      const statusColors = {
        [TicketStatus.Open]: {
          circle: 'bg-blue-500 dark:bg-blue-600 text-white border-blue-500 dark:border-blue-600 ring-2 ring-blue-300 dark:ring-blue-700',
          text: 'text-blue-700 dark:text-blue-400 font-semibold',
          line: 'bg-gray-300 dark:bg-gray-600',
        },
        [TicketStatus.InProgress]: {
          circle: 'bg-yellow-500 dark:bg-yellow-600 text-white border-yellow-500 dark:border-yellow-600 ring-2 ring-yellow-300 dark:ring-yellow-700',
          text: 'text-yellow-700 dark:text-yellow-400 font-semibold',
          line: 'bg-gray-300 dark:bg-gray-600',
        },
        [TicketStatus.Pending]: {
          circle: 'bg-orange-500 dark:bg-orange-600 text-white border-orange-500 dark:border-orange-600 ring-2 ring-orange-300 dark:ring-orange-700',
          text: 'text-orange-700 dark:text-orange-400 font-semibold',
          line: 'bg-gray-300 dark:bg-gray-600',
        },
        [TicketStatus.OnHold]: {
          circle: 'bg-purple-500 dark:bg-purple-600 text-white border-purple-500 dark:border-purple-600 ring-2 ring-purple-300 dark:ring-purple-700',
          text: 'text-purple-700 dark:text-purple-400 font-semibold',
          line: 'bg-gray-300 dark:bg-gray-600',
        },
        [TicketStatus.Resolved]: {
          circle: 'bg-green-500 dark:bg-green-600 text-white border-green-500 dark:border-green-600 ring-2 ring-green-300 dark:ring-green-700',
          text: 'text-green-700 dark:text-green-400 font-semibold',
          line: 'bg-gray-300 dark:bg-gray-600',
        },
        [TicketStatus.Closed]: {
          circle: 'bg-green-500 dark:bg-green-600 text-white border-green-500 dark:border-green-600 ring-2 ring-green-300 dark:ring-green-700',
          text: 'text-green-700 dark:text-green-400 font-semibold',
          line: 'bg-gray-300 dark:bg-gray-600',
        },
      };
      return statusColors[currentStatus] || statusColors[TicketStatus.Open];
    } else {
      // Future step
      return {
        circle: 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600',
        text: 'text-gray-500 dark:text-gray-400',
        line: 'bg-gray-300 dark:bg-gray-600',
      };
    }
  };

  // Define size-specific classes
  const sizeClasses = size === 'sm' ? {
    circle: 'w-8 h-8 sm:w-10 sm:h-10',
    icon: 'w-4 h-4 sm:w-5 sm:h-5',
    line: 'w-8 sm:w-10 h-0.5 sm:h-1',
    gap: 'gap-1.5 sm:gap-2',
    labelContainer: 'w-8 sm:w-10',
    lineSpacer: 'w-8 sm:w-10',
    labelText: 'text-xs sm:text-sm'
  } : {
    circle: 'w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10',
    icon: 'w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5',
    line: 'w-8 sm:w-10 md:w-14 h-0.5 sm:h-0.5 md:h-1',
    gap: 'gap-1 sm:gap-1.5',
    labelContainer: 'w-8 sm:w-9 md:w-10',
    lineSpacer: 'w-8 sm:w-10 md:w-14',
    labelText: 'text-xs sm:text-xs md:text-sm'
  };

  return (
    <div className={`flex flex-col items-center ${sizeClasses.gap}`}>
      {/* Icons and connecting lines row */}
      <div className="flex items-center">
        {steps.map((step, index) => {
          const colors = getStepColors(index);
          const Icon = step.icon;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              {/* Circle with icon */}
              <div
                className={`
                  ${sizeClasses.circle}
                  rounded-full border-2 flex items-center justify-center
                  transition-all duration-300 relative z-10
                  ${colors.circle}
                `}
              >
                <Icon className={sizeClasses.icon} />
              </div>
              {/* Connecting line - touching icons */}
              {!isLast && (
                <div className={`${sizeClasses.line} transition-all duration-300 ${colors.line}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Labels row - mirrors icon row structure */}
      <div className="flex items-center">
        {steps.map((step, index) => {
          const colors = getStepColors(index);
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={`label-${step.id}`}>
              {/* Label container matching icon width */}
              <div className={`${sizeClasses.labelContainer} flex justify-center`}>
                <span className={`${sizeClasses.labelText} transition-all duration-300 ${colors.text} whitespace-nowrap`}>
                  {step.label}
                </span>
              </div>
              {/* Spacer matching line width */}
              {!isLast && <div className={sizeClasses.lineSpacer} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
